// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { OUTCOME_COLOUR, OUTCOME_LABEL, OUTCOME_ORDER } from '../domain';
import { decadeSeries, yearSeries } from '../analysis';
import { stackedColumns, multiLine, type StackSeg, type StackDatum } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { formatNumber, formatPercent } from '../utils/format';

export function renderTime(root: HTMLElement, ctx: ViewContext): void {
  const { data } = ctx;
  const decs = decadeSeries(data.incidents, data.meta.dataThrough);
  const years = yearSeries(data.incidents);
  const n = data.meta.national;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">More incidents, fewer deaths</h1>
      <p class="view-sub">
        The number of recorded incidents has climbed for a century — partly because more people are in the water, partly
        because minor bites are reported far more thoroughly than they once were. But the
        ${glossaryTerm('fatality-rate', 'fatality rate')} has moved the other way.
      </p>
    </div>

    <div class="callout">
      <strong>From about 1 in 3 to about 1 in 10.</strong> Of incidents with a known outcome, roughly
      <strong>${formatPercent(n.fatalRateEarly ?? 0, 0)}</strong> were fatal before 1940, against about
      <strong>${formatPercent(n.fatalRateRecent ?? 0, 0)}</strong> since 2000 — even though the 2000s and 2010s
      recorded more incidents than any decades before them. Faster rescue, helicopter retrieval, and modern trauma
      medicine turned many once-fatal bites into survivable ones.
    </div>

    <div class="panel">
      <div class="panel-title">Incidents per decade, by outcome</div>
      <div class="panel-sub">The stack grows, but the red (fatal) band stays roughly flat — most of the growth is people surviving. Hover any column.</div>
      <div class="chart-scroll" data-role="decades"></div>
      <div class="legend" data-role="dec-legend"></div>
    </div>

    <div class="panel" style="margin-top:var(--space-lg)">
      <div class="panel-title">The share of incidents that prove fatal</div>
      <div class="panel-sub">Fatality rate per decade (decades with at least five known outcomes). A long slide from the 1800s to today.</div>
      <div class="chart-scroll" data-role="rate"></div>
    </div>

    <div class="panel" style="margin-top:var(--space-lg)">
      <div class="panel-title">Every year on record</div>
      <div class="panel-sub">Incidents per year, ${data.meta.dataFrom}–${data.meta.dataThrough}. The recent rise is real, but read it alongside better reporting.</div>
      <div class="chart-scroll" data-role="years"></div>
    </div>
  `;

  const segs: StackSeg[] = OUTCOME_ORDER.map((o) => ({ key: o, colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] }));
  const decData: StackDatum[] = decs.map((d) => ({
    id: String(d.decade),
    label: `${String(d.decade).slice(2)}`,
    values: { fatal: d.fatal, injured: d.injured, uninjured: d.uninjured, unknown: d.unknown },
    extraTip: `${formatPercent(d.fatalPct, 0)} fatal${d.partial ? ' · partial decade' : ''}`,
  }));
  (root.querySelector('[data-role="decades"]') as HTMLElement).appendChild(stackedColumns(decData, segs, { height: 340 }));
  (root.querySelector('[data-role="dec-legend"]') as HTMLElement).innerHTML = OUTCOME_ORDER.map(
    (o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]}</span>`,
  ).join('');

  // Fatality-rate line
  const rateDecs = decs.filter((d) => d.fatal + d.injured + d.uninjured >= 5);
  (root.querySelector('[data-role="rate"]') as HTMLElement).appendChild(
    multiLine(
      rateDecs.map((d) => `${String(d.decade).slice(2)}`),
      [{ id: 'rate', label: 'Fatal %', colour: OUTCOME_COLOUR.fatal, values: rateDecs.map((d) => d.fatalPct) }],
      { height: 300, yMax: 100, yFmt: (v) => `${v.toFixed(0)}%`, showEndLabel: false },
    ),
  );

  // Per-year totals
  (root.querySelector('[data-role="years"]') as HTMLElement).appendChild(
    stackedColumns(
      years.map((y) => ({ id: String(y.year), label: String(y.year), values: { fatal: y.fatal, other: y.total - y.fatal }, extraTip: `${formatNumber(y.total)} incidents · ${y.fatal} fatal` })),
      [
        { key: 'other', colour: '#8fbfc6', label: 'Non-fatal' },
        { key: 'fatal', colour: OUTCOME_COLOUR.fatal, label: 'Fatal' },
      ],
      { height: 300, labelEvery: Math.ceil(years.length / 12) },
    ),
  );
}
